import React, { useState, useEffect, useRef } from 'react'
import { AIService } from '../services/aiService'
import { useStoryStore } from '../stores/storyStore'
import toast from 'react-hot-toast'

const StoryCreationFlow = ({ onStoryCreated, onEditStory, initialContent = '', initialGenre = '', initialStyle = '' }) => {
  const { createStory } = useStoryStore()
  const [step, setStep] = useState(3) // 直接跳到创作步骤
  const [storyContent, setStoryContent] = useState(initialContent)
  const [selectedGenre, setSelectedGenre] = useState(initialGenre)
  const [selectedStyle, setSelectedStyle] = useState(initialStyle)
  const [isCreating, setIsCreating] = useState(true) // 直接开始创作
  const [progress, setProgress] = useState(0)
  const [progressText, setProgressText] = useState('准备开始创作...')
  const [createdStory, setCreatedStory] = useState(null)
  const [forceUpdate, setForceUpdate] = useState(0)
  const [hasStartedCreation, setHasStartedCreation] = useState(false)
  
  // 使用 useRef 来创建一个在组件生命周期内持久存在的标志，防止 React.StrictMode 重复调用
  const creationStartedRef = useRef(false)
  const componentIdRef = useRef(Math.random().toString(36).substr(2, 9))
  
  // 调试信息
  console.log(`StoryCreationFlow [${componentIdRef.current}] 状态:`, { step, isCreating, progress, hasStory: !!createdStory, forceUpdate, creationStarted: creationStartedRef.current })
  
  // 强制刷新函数
  const forceRerender = () => {
    setForceUpdate(prev => prev + 1)
  }
  
  // 监听状态变化，确保组件正确重新渲染
  useEffect(() => {
    console.log('状态变化:', { step, isCreating, progress, hasStory: !!createdStory, forceUpdate })
  }, [step, isCreating, progress, createdStory, forceUpdate])
  
  // 当故事创建完成时，强制更新步骤
  useEffect(() => {
    if (createdStory && progress === 100 && step === 3) {
      console.log('检测到故事完成但步骤未更新，强制更新步骤')
      setStep(4)
    }
  }, [createdStory, progress, step])
  
  // 组件加载时自动开始创作
  useEffect(() => {
    console.log(`[${componentIdRef.current}] useEffect 触发 - 检查创作条件:`, { 
      storyContent: !!storyContent, 
      selectedGenre: !!selectedGenre, 
      selectedStyle: !!selectedStyle, 
      isCreating, 
      progress, 
      hasStartedCreation,
      creationStartedRef: creationStartedRef.current
    })
    
    // 修复创作条件检查逻辑
    if (storyContent && selectedGenre && selectedStyle && !hasStartedCreation && !creationStartedRef.current) {
      console.log(`[${componentIdRef.current}] ✅ 满足创作条件，开始创作:`, { storyContent, selectedGenre, selectedStyle })
      // 先设置标志，再调用函数
      creationStartedRef.current = true
      setHasStartedCreation(true)
      // 延迟调用确保状态更新完成
      setTimeout(() => {
        handleCreateStory()
      }, 100)
    } else {
      console.log(`[${componentIdRef.current}] ❌ 不满足创作条件，跳过`)
    }
  }, [storyContent, selectedGenre, selectedStyle, hasStartedCreation]) // 移除 isCreating 和 progress 依赖

  // 当故事创建完成时，自动跳转到编辑器
  useEffect(() => {
    console.log('useEffect 触发 - 检查跳转条件:', { 
      hasStory: !!createdStory, 
      progress, 
      hasCallback: !!onEditStory,
      storyId: createdStory?.id 
    })
    
    if (createdStory && progress === 100 && onEditStory) {
      console.log('故事完成，准备跳转到编辑器')
      // 延迟跳转，确保状态更新完成
      const timer = setTimeout(() => {
        console.log('执行跳转:', createdStory.id)
        try {
          onEditStory(createdStory.id)
          console.log('跳转函数调用成功')
        } catch (error) {
          console.error('跳转失败:', error)
        }
      }, 500)
      
      return () => clearTimeout(timer)
    }
  }, [createdStory, progress, onEditStory])

  const handleNext = () => {
    if (step === 1 && storyContent.trim()) {
      setStep(2)
    } else if (step === 2 && selectedGenre && selectedStyle) {
      setStep(3)
      // 移除这里的 handleCreateStory() 调用，因为 useEffect 会自动处理
    }
  }

  const handleCreateStory = async () => {
    console.log(`[${componentIdRef.current}] 🎬 handleCreateStory 被调用，当前状态:`, { hasStartedCreation, isCreating, progress, creationStartedRef: creationStartedRef.current })
    
    // 防止重复调用的检查
    if (hasStartedCreation && creationStartedRef.current) {
      console.log(`[${componentIdRef.current}] ⚠️ 创作已开始，跳过重复调用`)
      return
    }
    
    if (isCreating && progress > 0) {
      console.log(`[${componentIdRef.current}] ⚠️ 创作已在进行中，跳过重复调用`)
      return
    }
    
    console.log(`[${componentIdRef.current}] 🚀 开始执行故事创作流程`)
    
    setIsCreating(true)
    setProgress(0)
    setProgressText('准备开始创作...')

    try {
      const story = await AIService.createCompleteStory(
        storyContent,
        selectedGenre,
        selectedStyle,
        (progressValue, progressMessage) => {
          setProgress(progressValue)
          setProgressText(progressMessage)
        }
      )

      console.log('故事创作完成，设置状态:', story)
      
      // 将AI创建的故事保存到store中
      const storyData = {
        title: story.title || 'AI创作的故事',
        description: story.summary || 'AI自动生成的故事',
        genre: story.genre || selectedGenre,
        style: story.style || selectedStyle,
        content: storyContent,
        fullStory: story // 保存完整的AI生成的故事数据
      }
      
      console.log('保存故事到store:', storyData)
      const savedStory = createStory(storyData)
      console.log('故事已保存到store:', savedStory)
      
      // 将AI生成的内容保存到localStorage中，以便编辑器使用
      localStorage.setItem(`ai-story-${savedStory.id}`, JSON.stringify(story))
      console.log('✅ AI故事数据已保存到localStorage:', {
        key: `ai-story-${savedStory.id}`,
        storyTitle: story.title,
        scenesCount: story.scenes?.length,
        charactersCount: story.characters?.length
      })
      
      // 添加详细的保存数据内容检查
      console.log('🔍 保存到localStorage的详细数据内容:')
      console.log('  📋 story.title:', story.title)
      console.log('  📋 story.summary:', story.summary)
      if (story.outline) {
        console.log('  📋 story.outline.title:', story.outline.title)
        console.log('  📋 story.outline.summary:', story.outline.summary?.substring(0, 100) + '...')
      }
      if (story.characters && story.characters.length > 0) {
        console.log('  👥 角色数据:', story.characters.map(c => ({name: c.name, role: c.role})))
      }
      if (story.chapters && story.chapters.length > 0) {
        console.log('  📚 章节数据:', story.chapters.map(ch => ({
          title: ch.title, 
          scenesCount: ch.scenes?.length || 0
        })))
      }
      
      // 详细调试AI数据结构
      console.log('🔍 详细AI数据结构:', {
        title: story.title,
        summary: story.summary,
        genre: story.genre,
        style: story.style,
        outline: story.outline,
        scenes: story.scenes?.map(s => ({
          id: s.id,
          title: s.title,
          content: s.content?.substring(0, 100) + '...',
          choices: s.choices?.length || 0,
          dialogues: s.dialogues?.length || 0
        })),
        characters: story.characters?.map(c => ({
          name: c.name,
          role: c.role,
          description: c.description?.substring(0, 50) + '...'
        }))
      })
      
      // 使用函数式更新确保状态正确设置
      setCreatedStory(savedStory)
      setIsCreating(false)
      
      // 立即设置步骤，不使用setTimeout
      console.log('立即设置步骤4')
      setStep(4)
      
      console.log('调用 onStoryCreated 回调')
      onStoryCreated && onStoryCreated(savedStory)
      
      // 强制刷新组件
      console.log('强制刷新组件')
      forceRerender()
      
      // 直接尝试跳转，不等待 useEffect
      console.log('直接尝试跳转')
      if (onEditStory) {
        setTimeout(() => {
          console.log('直接跳转执行:', savedStory.id)
          onEditStory(savedStory.id)
        }, 1000)
      }
      
    } catch (error) {
      console.error('创作失败:', error)
      setIsCreating(false)
      
      // 显示具体的错误信息给用户
      if (error.message.includes('API调用失败') || error.message.includes('生成失败')) {
        toast.error(`AI生成失败: ${error.message}`)
      } else if (error.message.includes('配额')) {
        toast.error('API配额已用完，请检查你的API配置或稍后重试')
      } else if (error.message.includes('网络')) {
        toast.error('网络连接失败，请检查网络连接后重试')
      } else {
        toast.error(`故事创作失败: ${error.message}`)
      }
    }
  }

  // 直接显示创作进度页面，跳过输入大纲和选择题材风格的步骤

  if (step === 3) {
    // 显示创作进度
    return (
      <div className="min-h-screen bg-dark text-gray-100 flex items-center justify-center">
        <div className="max-w-2xl mx-auto p-8 text-center">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-white mb-4">AI正在创作你的故事</h1>
            <p className="text-lg text-gray-300 mb-2">基于你的创意：</p>
            <p className="text-primary font-medium italic">"{storyContent}"</p>
            <div className="mt-4 flex justify-center gap-4 text-sm">
              <span className="bg-primary/20 text-primary px-3 py-1 rounded-full">题材：{selectedGenre}</span>
              <span className="bg-secondary/20 text-secondary px-3 py-1 rounded-full">风格：{selectedStyle}</span>
            </div>
            
            {/* API状态指示器 */}
            <div className="mt-4 flex justify-center">
              <div className="bg-dark-700/50 backdrop-blur-md rounded-lg p-3 border border-white/10">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-white">正在使用AI大模型生成内容</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  创作内容：8个镜头，2个分支，2个结局
                </p>
                <p className="text-xs text-gray-400">
                  如果API配额不足，将自动使用模拟数据
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-dark-light/50 backdrop-blur-md rounded-2xl p-8 border border-white/10">
            <div className="w-full bg-dark-600 rounded-full h-4 mb-6">
              <div
                className="bg-gradient-to-r from-primary to-secondary h-4 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-lg text-white mb-2">{progressText}</p>
            <p className="text-2xl font-bold text-primary">{progress}%</p>
            
            {progress === 100 && (
              <div className="mt-6">
                <p className="text-primary font-semibold text-lg">创作完成！正在跳转到编辑器...</p>
                <div className="mt-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (step === 4 && createdStory) {
    console.log('显示步骤4：故事完成页面')
    return (
      <div className="max-w-4xl mx-auto p-6 text-center">
        <h2 className="text-3xl font-bold text-white mb-4">故事创作完成！</h2>
        <div className="card mb-4">
          <h3 className="text-xl font-semibold mb-2 text-white">故事概览</h3>
          <div className="space-y-2 text-dark-300">
            <p>标题: <span className="text-white">{createdStory.metadata?.title}</span></p>
            <p>题材: <span className="text-white">{createdStory.metadata?.genre}</span></p>
            <p>风格: <span className="text-white">{createdStory.metadata?.style}</span></p>
            <p>镜头数: <span className="text-white">{createdStory.stats?.totalScenes}</span></p>
            <p>角色数: <span className="text-white">{createdStory.stats?.totalCharacters}</span></p>
            <p>分支数: <span className="text-white">{createdStory.stats?.totalBranches}</span></p>
            <p>结局数: <span className="text-white">{createdStory.stats?.totalEndings}</span></p>
            <p>复杂度: <span className="text-white">{createdStory.stats?.complexity}</span></p>
            <p>重玩价值: <span className="text-white">{createdStory.stats?.replayability}</span></p>
            <p>预计游戏时间: <span className="text-white">{createdStory.stats?.estimatedPlayTime}</span></p>
          </div>
        </div>
        <div className="space-y-3">
          <button
            onClick={() => {
              console.log('手动点击编辑故事按钮')
              if (onEditStory) {
                console.log('调用 onEditStory:', createdStory.id)
                onEditStory(createdStory.id)
              } else {
                console.log('onEditStory 回调不存在')
              }
            }}
            className="px-8 py-4 btn-primary block w-full"
          >
            编辑故事
          </button>
          <button
            onClick={() => {
              setStep(1)
              setCreatedStory(null)
              setIsCreating(false)
              setProgress(0)
              setHasStartedCreation(false) // 重置创作标志
              creationStartedRef.current = false // 重置 ref 标志
            }}
            className="px-6 py-3 btn-secondary"
          >
            创建新故事
          </button>
          <button
            onClick={() => {
              console.log('测试跳转按钮被点击')
              console.log('当前状态:', { step, createdStory: !!createdStory, progress })
              console.log('onEditStory 回调:', !!onEditStory)
            }}
            className="px-6 py-3 bg-yellow-600 text-white rounded-lg"
          >
            测试状态
          </button>
        </div>
      </div>
    )
  }
  
  // 如果故事已创建完成但步骤不是4，显示完成页面
  if (createdStory && progress === 100) {
    console.log('显示故事完成页面，当前步骤:', step)
    return (
      <div className="max-w-4xl mx-auto p-6 text-center">
        <h2 className="text-3xl font-bold text-white mb-4">故事创作完成！</h2>
        <div className="card mb-4">
          <h3 className="text-xl font-semibold mb-2 text-white">故事概览</h3>
          <div className="space-y-2 text-dark-300">
            <p>标题: <span className="text-white">{createdStory.metadata?.title}</span></p>
            <p>题材: <span className="text-white">{createdStory.metadata?.genre}</span></p>
            <p>风格: <span className="text-white">{createdStory.metadata?.style}</span></p>
            <p>镜头数: <span className="text-white">{createdStory.stats?.totalScenes}</span></p>
            <p>角色数: <span className="text-white">{createdStory.stats?.totalCharacters}</span></p>
            <p>分支数: <span className="text-white">{createdStory.stats?.totalBranches}</span></p>
            <p>结局数: <span className="text-white">{createdStory.stats?.totalEndings}</span></p>
            <p>复杂度: <span className="text-white">{createdStory.stats?.complexity}</span></p>
            <p>重玩价值: <span className="text-white">{createdStory.stats?.replayability}</span></p>
            <p>预计游戏时间: <span className="text-white">{createdStory.stats?.estimatedPlayTime}</span></p>
          </div>
        </div>
        <div className="space-y-3">
          <button
            onClick={() => onEditStory && onEditStory(createdStory.id)}
            className="px-8 py-4 btn-primary block w-full"
          >
            编辑故事
          </button>
          <button
            onClick={() => {
              setStep(1)
              setCreatedStory(null)
              setIsCreating(false)
              setProgress(0)
              setHasStartedCreation(false) // 重置创作标志
              creationStartedRef.current = false // 重置 ref 标志
            }}
            className="px-6 py-3 btn-secondary"
          >
            创建新故事
          </button>
        </div>
      </div>
    )
  }

  // 备用显示 - 如果状态不一致，显示调试信息
  return (
    <div className="max-w-4xl mx-auto p-6 text-center">
      <h2 className="text-3xl font-bold text-white mb-4">状态异常</h2>
      <div className="card mb-4">
        <div className="space-y-2 text-dark-300">
          <p>当前步骤: <span className="text-white">{step}</span></p>
          <p>正在创作: <span className="text-white">{isCreating ? '是' : '否'}</span></p>
          <p>进度: <span className="text-white">{progress}%</span></p>
          <p>有故事数据: <span className="text-white">{createdStory ? '是' : '否'}</span></p>
        </div>
        {createdStory && (
          <div className="mt-4 text-left">
            <h4 className="font-semibold text-white">故事数据:</h4>
            <pre className="text-xs bg-dark-700 p-2 rounded overflow-auto text-white border border-dark-600">
              {JSON.stringify(createdStory, null, 2)}
            </pre>
          </div>
        )}
      </div>
      <button
        onClick={() => {
          setStep(1)
          setCreatedStory(null)
          setIsCreating(false)
          setProgress(0)
          setHasStartedCreation(false) // 重置创作标志
        }}
        className="px-6 py-3 btn-secondary"
      >
        重新开始
      </button>
    </div>
  )
}

export default StoryCreationFlow
